import React from 'react';
import { SectionTitle } from '../SectionTitle';
import TDTGraph from '../../special/TDTGraph';
import SISIGraph from '../../special/SISIGraph';
import ABLBGraph from '../../special/ABLBGraph';
import {
  TDT_FREQS, SISI_FREQS, ABLB_INTENSITIES,
  normaliseSpecial, interpretTDT, interpretSISI, interpretABLB, overallConclusion,
} from '../../special/specialLogic';

/**
 * SpecialReportSection — compact renderer used by the standard hearing
 * report layout. Falls back silently to the generic renderer when the
 * saved data only has the pre-rebuild `{fields: {...}}` bag.
 */
export const SpecialReportSection = ({ data }) => {
  const s = normaliseSpecial(data);
  const hasNew = Object.keys(s.tdt.values || {}).length > 0
    || Object.keys(s.sisi.right || {}).length > 0
    || Object.keys(s.sisi.left  || {}).length > 0
    || Object.keys(s.ablb.right || {}).length > 0
    || Object.keys(s.ablb.left  || {}).length > 0
    || s.overall_conclusion || s.recommendations;
  if (!hasNew) return null;

  const tRes = interpretTDT(s);
  const sRes = interpretSISI(s);
  const aRes = interpretABLB(s);
  const overall = overallConclusion(tRes, sRes, aRes);

  const badge = (tone) => tone === 'neg' ? 'bg-emerald-600' : tone === 'pos' ? 'bg-red-600' : tone === 'mid' ? 'bg-amber-500' : 'bg-slate-400';

  return (
    <div data-testid="special-report-section">
      <SectionTitle>Special Tests · TDT | SISI | ABLB</SectionTitle>
      <div className="grid grid-cols-3 gap-1.5 mb-1">
        <div className="border border-slate-300 rounded p-1">
          <div className="text-[9px] font-bold text-sky-700 uppercase mb-0.5">TDT</div>
          <TDTGraph values={s.tdt.values} readOnly width={220} height={110} />
          <div className="text-[8px] mt-0.5 flex items-start gap-1">
            <span className={`inline-block w-2.5 h-2.5 rounded-full mt-0.5 ${badge(tRes.tone)}`} />
            <span className="font-bold">{tRes.label}</span>
          </div>
        </div>
        <div className="border border-slate-300 rounded p-1">
          <div className="text-[9px] font-bold text-sky-700 uppercase mb-0.5">SISI</div>
          <SISIGraph right={s.sisi.right} left={s.sisi.left} readOnly width={220} height={110} />
          <div className="text-[8px] mt-0.5 flex items-start gap-1">
            <span className={`inline-block w-2.5 h-2.5 rounded-full mt-0.5 ${badge(sRes.tone)}`} />
            <span className="font-bold">{sRes.label}</span>
          </div>
        </div>
        <div className="border border-slate-300 rounded p-1">
          <div className="text-[9px] font-bold text-sky-700 uppercase mb-0.5">ABLB</div>
          <ABLBGraph right={s.ablb.right} left={s.ablb.left} readOnly width={220} height={110} />
          <div className="text-[8px] mt-0.5 flex items-start gap-1">
            <span className={`inline-block w-2.5 h-2.5 rounded-full mt-0.5 ${badge(aRes.tone)}`} />
            <span className="font-bold">{aRes.label}</span>
          </div>
        </div>
      </div>
      <div className="text-[10px] mb-0.5" data-testid="special-report-section-overall">
        <span className="font-bold text-blue-800 uppercase tracking-wide text-[9px] mr-1">Overall Conclusion:</span>
        <span className="font-semibold">{overall.label}</span>
        <span className="ml-2 text-slate-700">{s.overall_conclusion || overall.note}</span>
      </div>
      {s.recommendations && (
        <div className="text-[10px]">
          <span className="font-bold text-blue-800 uppercase tracking-wide text-[9px] mr-1">Recommendations:</span>
          <span className="whitespace-pre-wrap">{s.recommendations}</span>
        </div>
      )}
    </div>
  );
};

export default SpecialReportSection;
